// ConfirmModal.tsx — CTSK-402. Generic yes/no confirmation dialog for
// destructive-ish actions that don't need PurgeConfirmModal's heavier
// type-to-confirm gate (that one's reserved for irreversible hard
// deletes — see its header comment). This is the "are you sure?"
// shape: Cancel / Confirm buttons, Escape cancels, Enter confirms,
// overlay click cancels. Same dialog conventions as
// AuditHistoryDrawer / CleanRegistryModal (role="dialog", aria-modal,
// fixed-inset overlay).
//
// First caller: DocumentDetail's Delete action (CTSK-402), replacing
// a native `confirm()` popup that couldn't be keyboard-navigated
// consistently across browsers and broke the page's visual chrome.

import { useEffect } from 'react';

export function ConfirmModal({
  open,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  danger = false,
  isPending = false,
  onConfirm,
  onCancel,
}: {
  /** Whether the modal is visible. Callers should unmount/condition-render
   *  rather than pass `open=false` — this component always renders its
   *  overlay when mounted, matching PurgeConfirmModal's contract. */
  open: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Styles the confirm button with the danger palette (destructive actions). */
  danger?: boolean;
  isPending?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  // Escape cancels, Enter confirms — unless a call is in flight (same
  // busy-guard convention as CleanRegistryModal).
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (isPending) return;
      if (e.key === 'Escape') onCancel();
      else if (e.key === 'Enter') onConfirm();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, isPending, onConfirm, onCancel]);

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={onCancel}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--surface-raised)', borderRadius: 'var(--radius-md)',
          border: `1px solid ${danger ? 'var(--danger-primary, #B3261E)' : 'var(--border-strong)'}`,
          padding: 'var(--space-6)', maxWidth: 440, width: '90%',
        }}
      >
        <h3 style={{ margin: '0 0 var(--space-2) 0', fontSize: 16 }}>{title}</h3>
        <p style={{ margin: '0 0 var(--space-5) 0', fontSize: 13, color: 'var(--text-secondary)' }}>
          {message}
        </p>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 'var(--space-2)' }}>
          <button type="button" className="btn" onClick={onCancel} disabled={isPending}>
            {cancelLabel}
          </button>
          <button
            type="button"
            className="btn"
            onClick={onConfirm}
            disabled={isPending}
            autoFocus
            style={{
              background: danger ? 'var(--danger-primary, #B3261E)' : undefined,
              color: danger ? '#fff' : undefined,
              borderColor: danger ? 'var(--danger-primary, #B3261E)' : undefined,
              cursor: isPending ? 'not-allowed' : 'pointer',
            }}
          >
            {isPending ? 'Working…' : confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
